/**
 * First-load / navigation busy signal. Visible while a query has no data yet
 * (`pending`); autosave refetches stay silent (`success` + fetching).
 * Never captures pointer — a hung request must not freeze navigation.
 */
import { CORE_MESSAGES } from '@daka/core';
import { useIsFetching } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useDebounce } from 'use-debounce';

import { useT } from '@/i18n/useT';

const SHOW_DELAY_MS = 120;

export function ApiActivitySpinner() {
   const t = useT();
   const fetching = useIsFetching({ predicate: (query) => query.state.status === 'pending' });
   const busy = fetching > 0;
   const [debouncedBusy] = useDebounce(busy, SHOW_DELAY_MS);
   const show = busy && debouncedBusy;

   if (!show) return null;

   const label = t(CORE_MESSAGES.loading);

   return (
      <div className="pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center pt-16" aria-busy="true">
         <div
            role="status"
            aria-live="polite"
            aria-label={label}
            className="flex items-center gap-2 rounded-lg border border-border bg-background/95 px-3 py-2 text-sm text-foreground shadow-md"
         >
            <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
            <span>{label}</span>
         </div>
      </div>
   );
}
